import { Component, OnInit, OnDestroy } from '@angular/core';

import { AssetStatuses } from 'app/models/control-center/asset.model';
import { Person } from 'app/models/control-center/person.model';
import { Site } from 'app/models/control-center/site.model';
import { Warehouse } from 'app/models/control-center/warehouse.model';
import { HttpService } from 'app/services/http.service';
import { ScannerService } from 'app/services/scanner.service';
import { Utils } from 'app/utils';
import { catchError, concatMap, of, Subscription } from 'rxjs';

export interface ScannerResult {
    serialNumber: string;
    result: string;
    success: boolean;
    pending: boolean;
    time: number;
}

interface BulkUpdateAssetStatusesResponse {
    errors: Array<{
        reason: {
            message: string;
            name?: string;
            stack?: string;
        };
    }>;
    completed: Array<{
        /** The asset id. */
        result: number;
    }>;
}

@Component({
    selector: 'app-scanner',
    templateUrl: './scanner.component.html',
    styleUrls: ['./scanner.component.scss'],
})
export class ScannerComponent implements OnInit, OnDestroy {
    loading = false;
    scanning = false;
    scanRunning = false;
    private scanSubscription: Subscription | null = null;

    private processedSerialNumbers: Set<string> = new Set<string>();
    private failedSerialNumbers: Set<string> = new Set<string>();

    scanResults: ScannerResult[] = [];
    scanResultsSuccessful: number = 0;
    scanResultsFailed: number = 0;

    bulkUpdateStatuses: {
        status?: AssetStatuses;
        warehouseId?: number;
        warehouseLocation?: string;
        personId?: number;
        siteId?: number;
    } = {};

    assetStatuses = AssetStatuses;

    warehouses: Warehouse[] = [];
    people: Person[] = [];
    sites: Site[] = [];

    constructor(
        private readonly _httpService: HttpService,
        private readonly _scannerService: ScannerService,
    ) { }

    formatPersonDisplay = (person: any) => Utils.formatPersonDisplay(person);

    async ngOnInit() {

        await Promise.all([
            (async () => this.warehouses = await this._httpService.get('inventory/warehouses'))(),
            (async () => this.people = await this._httpService.get('people'))(),
            (async () => this.sites = await this._httpService.get('companies/sites'))(),
        ]);
    }

    ngOnDestroy() {
        if (this.scanSubscription) {
            this.scanSubscription.unsubscribe();
        }
    }

    get selectedSiteName(): string {
        return this.sites.find(s => s.id === this.bulkUpdateStatuses.siteId)?.name ?? '';
    }

    get selectedPersonName(): string {
        const person = this.people.find(p => p.id === this.bulkUpdateStatuses.personId);
        return person ? this.formatPersonDisplay(person) : '';
    }

    private addScanResult(serialNumber: string, success: boolean, pending: boolean, result: string, update: boolean = false): void {
        let updated = false;
        if (update) {
            const index = this.scanResults.findIndex(r => r.serialNumber === serialNumber && r.pending === true);
            if (index !== -1) {
                this.scanResults[index] = { serialNumber, success, pending, result, time: Date.now() };
                updated = true;
            }
        }
        if (!updated) this.scanResults.unshift({
            serialNumber,
            success,
            pending,
            result,
            time: Date.now(),
        });

        if (success) {
            this.scanResultsSuccessful += 1;
        } else if (!pending) {
            this.scanResultsFailed += 1;
        }
    }

    subscribeToScanResults() {
        this.scanSubscription = this._scannerService.onScan$
            // concatMap to buffer and avoid racing.
            .pipe(
                concatMap(serialNumber => this.processScanResult(serialNumber)),
                catchError((error: unknown) => {
                    console.error('Error processing scan result:', error);
                    return of();
                }),
            )
            .subscribe();
    }

    private getUpdateFields() {
        const returning = {
            status: this.bulkUpdateStatuses.status,
            warehouseId: null,
            warehouseLocation: null,
            personId: null,
            siteId: null,
        };
        switch (this.bulkUpdateStatuses.status) {
            case AssetStatuses.Provisioning:
            case AssetStatuses.Maintenance:
                returning.warehouseId = this.bulkUpdateStatuses.warehouseId;
                break;
            case AssetStatuses.CheckedOut:
                returning.siteId = this.bulkUpdateStatuses.siteId;
                returning.personId = this.bulkUpdateStatuses.personId;
                break;
            case AssetStatuses.Available:
                returning.warehouseId = this.bulkUpdateStatuses.warehouseId;
                returning.warehouseLocation = this.bulkUpdateStatuses.warehouseLocation;
                break;
        }
        return returning;
    }

    async processScanResult(serialNumber: string) {
        if (!serialNumber || serialNumber.trim().length === 0) return;
        if (this.processedSerialNumbers.has(serialNumber) && !this.failedSerialNumbers.has(serialNumber)) {
            this.addScanResult(serialNumber, false, false, 'Duplicate Scan');
            return;
        }
        this.processedSerialNumbers.add(serialNumber);
        this.addScanResult(serialNumber, false, true, 'processing', true);
        const asset = await this._httpService.get(`inventory/assets?serialNumber=${serialNumber}`);

        if (!asset?.id) {
            this.addScanResult(serialNumber, false, false, 'Invalid Serial Number', true);
            return;
        }

        const isRetry = this.failedSerialNumbers.has(serialNumber);
        try {

            if (isRetry) {
                this.failedSerialNumbers.delete(serialNumber);
            }

            // update the asset with the new status
            const updateResult: BulkUpdateAssetStatusesResponse = await this._httpService.put('inventory/assets/statuses', {
                ...this.getUpdateFields(),
                assetIds: [asset.id],
            });

            // only sent 1 asset
            const error = updateResult?.errors?.[0];
            const wasCompleted = updateResult?.completed?.[0]?.result === asset.id;
            if (error || !wasCompleted) {
                const reason = `Error updating asset ${asset.serialNumber} status: ${error?.reason?.message || 'Asset update not completed'}`;
                throw new Error(reason, { cause: error?.reason });
            }

            // add the result to the scan results
            this.addScanResult(asset.serialNumber, true, false, isRetry ? 'Successfully Updated (Retry)' : 'Successfully Updated', true);
        } catch (error) {
            console.error(error);
            // if we get an error, tag as invalid and exit
            // this.processedSerialNumbers.delete(asset.serialNumber);
            // mark the serial as failed
            this.failedSerialNumbers.add(asset.serialNumber);
            // add the result to the scan results
            this.addScanResult(asset.serialNumber, false, false, 'Update Error - Scan again to retry', true);
        }
    }

    start() {
        this.processedSerialNumbers = new Set<string>();
        this.failedSerialNumbers = new Set<string>();

        this.scanResults = [];
        this.scanResultsFailed = 0;
        this.scanResultsSuccessful = 0;

        Utils.characters = [];
        this.scanning = true;
        this.subscribeToScanResults();
    }

    stop() {
        this.scanning = false;
        this.scanSubscription.unsubscribe();
        this.scanSubscription = null;
    }
}
